import { useEffect, useRef } from "react";
import { Button, Header, Icon, Label, Segment } from "semantic-ui-react";
import useBill from "../../../hooks/useBill";
import { useAuth } from "../../../context/AuthContext";
import { hasCapability } from "../../../utils/capabilities";
import { formatRupees } from "../../../utils/money";
import { fmtDateTime } from "../adminHelpers";
import { BILLABLE_BOOKING_STATUSES, STATUS_LABEL, finalizeBlockers } from "./BillView";
import { KIND_LABELS, MODE_OPTIONS } from "./BillPayments";
import { PaidLabel } from "./BillStatusExtras";

const RECENT = 3;

/* The booking's money at a glance — the bill's total, what has been paid
   (advances included, before any bill exists) and what is still owed — and
   the way to the bill page. Mounted fresh whenever the booking screen is,
   so coming back from the bill page shows the latest figures. Imports no
   router: it sits inside screens that many tests render, and CRA's Jest
   cannot load react-router v7 (see src/App.test.js). */
export default function BillCard({ booking, onOpenBill }) {
  const { currentUser } = useAuth();
  const can = (cap) => hasCapability(currentUser, cap);
  const billState = useBill(booking.id, { enabled: can("bill.view") });
  const { bill, noBill, loading, error, payments, paymentSummary: s, reload } = billState;

  /* A check-out, room change or extension elsewhere on this screen changes
     what the bill should say; reload so "refresh charges" shows when due. */
  const seenUpdate = useRef(booking.updatedAt);
  useEffect(() => {
    if (seenUpdate.current === booking.updatedAt) return;
    seenUpdate.current = booking.updatedAt;
    reload();
  }, [booking.updatedAt, reload]);

  if (!can("bill.view")) return null;
  const live = payments.filter((p) => !p.isVoid);
  if (!bill && !live.length && !BILLABLE_BOOKING_STATUSES.has(booking.status)) return null;

  /* The screen that hosts the card owns navigation (onOpenBill); without it
     the button is still a plain link to the same page. */
  const billPath = `/gh-admin/bill/${booking.id}`;
  const openBill = (e) => {
    if (!onOpenBill) return;
    e.preventDefault();
    onOpenBill(booking.id);
  };
  const todo = bill?.status === "DRAFT" ? finalizeBlockers(bill) : [];
  const recent = live.slice(-RECENT).reverse();
  const modeText = (m) => MODE_OPTIONS.find((o) => o.value === m)?.text || m;

  return (
    <Segment className="bhu-rail-card">
      <Header as="h5" icon="rupee sign" content="Bill & Payments" />
      {loading && !bill && !noBill && <span style={{ opacity: 0.6 }}>Loading…</span>}
      {error && <div style={{ color: "#9f3a38", marginBottom: 6 }}>{error}</div>}

      {bill ? (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <span>
            {bill.billNumber} <Label size="mini">{STATUS_LABEL[bill.status]}</Label>
            <PaidLabel bill={bill} summary={s} size="mini" />
          </span>
          <strong style={{ fontSize: "1.15em" }}>{formatRupees(bill.grandTotal)}</strong>
        </div>
      ) : (
        noBill && <div style={{ opacity: 0.75 }}>No bill yet.</div>
      )}

      {recent.length > 0 && (
        <div style={{ marginTop: 6, fontSize: "0.88em" }}>
          {recent.map((p) => (
            <div key={p.id} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span>
                {KIND_LABELS[p.kind]} · {modeText(p.mode)}
                {p.receiptNumber && ` · slip ${p.receiptNumber}`}
                <span style={{ opacity: 0.6 }}> · {fmtDateTime(p.receivedAt)}</span>
              </span>
              <span>{p.kind === "REFUND" ? "− " : ""}{formatRupees(p.amount)}</span>
            </div>
          ))}
          {live.length > RECENT && <div style={{ opacity: 0.6 }}>and {live.length - RECENT} earlier</div>}
        </div>
      )}

      {s && (Number(s.netPaid) > 0 || bill) && (
        <div style={{ marginTop: 6, borderTop: "1px solid rgba(34,36,38,.1)", paddingTop: 6 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Paid so far</span><strong>{formatRupees(s.netPaid)}</strong>
          </div>
          {bill && Number(s.refundDue) > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", color: "#9f3a38" }}>
              <span>To refund</span><strong>{formatRupees(s.refundDue)}</strong>
            </div>
          )}
          {bill && Number(s.refundDue) === 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", color: s.settled ? "#21ba45" : "#b35c00" }}>
              <span>{s.settled ? "Settled" : "Still to pay"}</span>
              {!s.settled && <strong>{formatRupees(s.balanceDue)}</strong>}
            </div>
          )}
        </div>
      )}

      {todo.length > 0 && (
        <div style={{ fontSize: "0.85em", color: "#b35c00", marginTop: 4 }}>
          <Icon name="exclamation circle" />{todo[0]}
        </div>
      )}

      <Button as="a" href={billPath} size="small" primary style={{ marginTop: 8 }} onClick={openBill}>
        <Icon name={bill ? "folder open outline" : "plus"} />
        {bill ? "Open bill" : can("payment.record") ? "Take advance / make bill" : "Open"}
      </Button>
    </Segment>
  );
}
